import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Minus, Plus, Truck, RotateCcw, ShieldCheck, ArrowRight, ChevronDown, CreditCard, Headset, BadgeCheck } from "lucide-react";
import { storeApi, formatPrice } from "@/lib/storefront";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { useRecentlyViewed } from "@/contexts/RecentlyViewedContext";
import ProductCard from "@/components/storefront/ProductCard";
import ProductGallery from "@/components/storefront/ProductGallery";
import ProductVariants from "@/components/storefront/ProductVariants";
import SocialShare from "@/components/storefront/SocialShare";
import RecentlyViewed from "@/components/storefront/RecentlyViewed";
import FrequentlyBoughtTogether from "@/components/storefront/FrequentlyBoughtTogether";
import SEO from "@/components/storefront/SEO";
import JsonLd, { productSchema, breadcrumbSchema } from "@/components/storefront/JsonLd";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const { customer } = useCustomerAuth();
  const tradePct = customer && customer.wholesale_approved ? (customer.trade_discount_percent || 25) : 0;
  const { track } = useRecentlyViewed();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState("specs");
  const [added, setAdded] = useState(false);
  const [waitEmail, setWaitEmail] = useState("");
  const [waitSending, setWaitSending] = useState(false);
  const [waitJoined, setWaitJoined] = useState(false);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitEmail || !waitEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setWaitSending(true);
    try {
      const { data } = await storeApi.post("/waitlist", { email: waitEmail, product_id: product.id });
      toast.success(data.message || "You're on the list");
      setWaitJoined(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setWaitSending(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    storeApi.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setQty(1);
        setAdded(false);
        track(r.data);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug, track]);

  if (loading) return <div className="mx-auto max-w-6xl px-6 py-16"><div className="h-[600px] animate-pulse rounded-md bg-[#F1E3DC]" /></div>;
  if (!product) return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <SEO title="Not found" noindex />
      <h1 className="font-display text-5xl">Product not found</h1>
      <p className="mt-3 text-[#6B5158]">This piece may have moved or sold out.</p>
      <Link to="/catalog" className="mt-8 inline-block rounded-full bg-[#2B2127] px-6 py-3 text-sm text-white">Explore the catalog</Link>
    </div>
  );

  const hasSale = product.old_price && product.old_price > product.price;
  const comingSoon = !!product.coming_soon;
  const soldOut = (product.stock || 0) <= 0 && !product.allow_backorder && !comingSoon;
  const images = product.images?.length ? product.images : (product.primary_image ? [product.primary_image] : []);
  const attributes = product.attributes || {};
  const isWished = has(product.id);

  const handleAdd = () => {
    if (comingSoon) { toast.info("This item is coming soon — join the waitlist above"); return; }
    if (soldOut) { toast.error("Out of stock"); return; }
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };
  const buyNow = () => {
    if (comingSoon) { toast.info("This item is coming soon — join the waitlist above"); return; }
    if (soldOut) { toast.error("Out of stock"); return; }
    addItem(product, qty);
    setTimeout(() => nav("/checkout"), 200);
  };

  return (
    <div data-testid="product-page">
      <SEO
        title={product.seo_title || product.title}
        description={product.seo_description || product.short_description || product.description?.slice(0, 160)}
        image={product.primary_image}
        type="product"
      />
      <JsonLd data={productSchema(product, typeof window !== "undefined" ? window.location.href : "")} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Shop", url: "/catalog" },
        ...(product.category ? [{ name: product.category.name, url: `/c/${product.category.slug}` }] : []),
        { name: product.title, url: typeof window !== "undefined" ? window.location.pathname : undefined },
      ])} />
      <div className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 sm:py-10 md:px-10 md:py-14">
        {/* Breadcrumbs — premium, readable */}
        <nav className="mb-6 flex flex-wrap items-center gap-y-1 text-[10px] font-medium uppercase tracking-[0.18em] text-[#6B5158] sm:mb-10 sm:text-[12px] sm:tracking-[0.28em]">
          <Link to="/" className="hover:text-black">Home</Link>
          <span className="mx-2 text-[#9D5C6C] sm:mx-2.5">/</span>
          <Link to="/catalog" className="hover:text-black">Shop</Link>
          {product.category && <><span className="mx-2 text-[#9D5C6C] sm:mx-2.5">/</span><Link to={`/c/${product.category.slug}`} className="hover:text-black">{product.category.name}</Link></>}
          <span className="mx-2 text-[#9D5C6C] sm:mx-2.5">/</span>
          <span className="text-black/65">{product.title.slice(0, 40)}</span>
        </nav>

        <div className="grid gap-8 md:grid-cols-2 md:gap-12 lg:gap-20">
          {/* Gallery */}
          <ProductGallery images={images} title={product.title} />

          {/* Info */}
          <div className="min-w-0">
            {product.brand?.name && (
              <Link to={`/brand/${product.brand.slug}`} className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#6B5158] hover:text-[#9D5C6C] sm:text-[13px] sm:tracking-[0.32em]" data-testid="product-brand">
                {product.brand.name}
              </Link>
            )}
            <h1
              className="mt-3 break-words text-[22px] leading-[1.25] text-black sm:text-[26px] md:text-[28px] lg:text-[32px] lg:leading-[1.2]"
              style={{
                letterSpacing: "-0.005em",
                fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontWeight: 600,
                fontVariationSettings: "normal",
              }}
              data-testid="product-title"
            >
              {/* Insert a thin breakable space after commas / slashes that have
               *  no whitespace so long Amazon-style titles wrap nicely instead
               *  of running together. */}
              {product.title.replace(/,(\S)/g, ", $1").replace(/\/(\S)/g, " / $1")}
            </h1>

            <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1 store-nums sm:mt-6">
              <span className="text-2xl font-semibold text-black sm:text-[28px]">{formatPrice(product.price, product.currency)}</span>
              {hasSale && (
                <>
                  <span className="text-base text-[#5D5448] line-through sm:text-lg">{formatPrice(product.old_price, product.currency)}</span>
                  <span className="rounded-full bg-black px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white sm:px-2.5 sm:text-[11px] sm:tracking-[0.15em]">Save {Math.round((1 - product.price / product.old_price) * 100)}%</span>
                </>
              )}
            </div>

            {tradePct > 0 && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-[#6D2E4B]/30 bg-[#F9F1EB] px-3.5 py-2 text-[13px] text-[#3A2E33]" data-testid="trade-price-banner">
                <span className="rounded-full bg-[#6D2E4B] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">Trade</span>
                Your price: <span className="font-semibold text-[#6D2E4B] store-nums">{formatPrice(product.price * (1 - tradePct / 100), product.currency)}</span>
                <span className="text-[#6B5158]">(−{tradePct}% wholesale, applied at checkout)</span>
              </div>
            )}

            {product.short_description && (
              <p className="mt-5 max-w-xl text-[14.5px] leading-[1.7] text-[#3A2E33] sm:mt-6 sm:text-[15.5px]" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                {product.short_description.replace(/,(\S)/g, ", $1").replace(/\/(\S)/g, " / $1")}
              </p>
            )}

            {/* Variants */}
            <ProductVariants attributes={attributes} />

            <div className="mt-8">
              <div className={`text-[12px] font-semibold uppercase tracking-[0.22em] ${comingSoon ? "text-[#6D2E4B]" : soldOut ? "text-rose-600" : "text-[#7A4A58]"}`}>
                <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: comingSoon ? "#6D2E4B" : soldOut ? "currentColor" : "#9D5C6C" }} />
                {comingSoon ? "Coming soon — arriving shortly" : soldOut ? "Out of stock" : "In stock — ready to ship"}
              </div>
            </div>

            {comingSoon ? (
              <div className="mt-6" data-testid="waitlist-box">
                {waitJoined ? (
                  <div className="rounded-md border border-[#E6D4CC] bg-[#F9F1EB] p-5 text-[15px] leading-relaxed text-[#3A2E33]" data-testid="waitlist-success">
                    <span className="font-semibold text-[#6D2E4B]">You're on the list.</span> We'll email you the moment this item lands on our shelves — one email, no spam.
                  </div>
                ) : (
                  <>
                    <form onSubmit={joinWaitlist} className="flex flex-col gap-2 sm:flex-row sm:items-center" data-testid="waitlist-form">
                      <input
                        type="email"
                        value={waitEmail}
                        onChange={(e) => setWaitEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="flex-1 rounded-full border border-[#E6D4CC] bg-white px-5 py-3.5 text-sm text-[#2B2127] outline-none placeholder:text-[#6B5158]/60 focus:border-[#9D5C6C]"
                        data-testid="waitlist-email-input"
                      />
                      <button
                        type="submit"
                        disabled={waitSending}
                        className="rounded-full bg-[#6D2E4B] px-7 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#59253d] disabled:opacity-50 sm:text-[15px]"
                        data-testid="waitlist-submit-btn"
                      >
                        {waitSending ? "…" : "Notify me"}
                      </button>
                      <button type="button" onClick={() => toggle(product)} aria-label="Wishlist" className="hidden flex-shrink-0 rounded-full border border-[#C7BFAB] bg-white p-3.5 hover:bg-[#F1E3DC] sm:block" data-testid="product-wishlist-btn">
                        <Heart size={16} className={isWished ? "fill-[#9D5C6C] text-[#9D5C6C]" : "text-black"} />
                      </button>
                    </form>
                    <p className="mt-3 text-[13px] leading-relaxed text-[#6B5158]">
                      Join the waitlist and we'll email you the moment it lands — no charge today, no obligation, unsubscribe anytime.
                    </p>
                  </>
                )}
              </div>
            ) : (
            <>
            {/* Qty + Add */}
            <div className="mt-6 flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1 rounded-full border border-[#C7BFAB] bg-white">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 text-black sm:px-4" aria-label="Decrease"><Minus size={14} /></button>
                <span className="min-w-[24px] text-center text-base font-semibold text-black store-nums sm:min-w-[28px]" data-testid="product-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3 text-black sm:px-4" aria-label="Increase"><Plus size={14} /></button>
              </div>
              <button
                onClick={handleAdd}
                disabled={soldOut}
                className={`min-w-0 flex-1 rounded-full px-4 py-3.5 text-[14px] font-semibold tracking-[0.02em] text-white transition-all sm:px-6 sm:py-4 sm:text-[15px] ${added ? "bg-[#9D5C6C]" : "bg-black hover:bg-[#9D5C6C]"} disabled:bg-[#5D5448]/40`}
                data-testid="add-to-bag-btn"
              >
                {soldOut ? "Sold out" : (added ? "✓ Added" : "Add to bag")}
              </button>
              <button onClick={() => toggle(product)} aria-label="Wishlist" className="flex-shrink-0 rounded-full border border-[#C7BFAB] bg-white p-3 hover:bg-[#F1E3DC] sm:p-3.5" data-testid="product-wishlist-btn">
                <Heart size={16} className={isWished ? "fill-[#9D5C6C] text-[#9D5C6C]" : "text-black"} />
              </button>
            </div>
            <button
              onClick={buyNow}
              disabled={soldOut}
              className="mt-2 w-full rounded-full border border-black px-6 py-4 text-[15px] font-semibold tracking-[0.02em] text-black hover:bg-black hover:text-white disabled:opacity-40"
              data-testid="buy-now-btn"
            >
              Buy it now
            </button>
            </>
            )}

            {/* Share row */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#C7BFAB] pt-5 sm:mt-7 sm:pt-6">
              <SocialShare url={typeof window !== "undefined" ? window.location.href : ""} title={product.title} image={product.primary_image} />
              <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-[#6B5158] sm:text-[11px] sm:tracking-[0.22em]">
                <CreditCard size={13} />
                <span>Visa · MC · Amex</span>
              </div>
            </div>

            {/* Trust */}
            <div className="mt-6 grid grid-cols-2 gap-3 border-t border-[#C7BFAB] pt-6 text-[10px] font-semibold uppercase leading-[1.35] tracking-[0.14em] text-[#3A2E33] sm:mt-7 sm:grid-cols-3 sm:gap-5 sm:pt-7 sm:text-[11px] sm:tracking-[0.18em] md:grid-cols-5" data-testid="product-trust-row">
              <div className="flex items-start gap-2"><Truck size={15} className="mt-0.5 flex-shrink-0 text-[#9D5C6C]" /><div>Fast<br />delivery</div></div>
              <div className="flex items-start gap-2"><RotateCcw size={15} className="mt-0.5 flex-shrink-0 text-[#9D5C6C]" /><div>30-day<br />returns</div></div>
              <div className="flex items-start gap-2"><ShieldCheck size={15} className="mt-0.5 flex-shrink-0 text-[#9D5C6C]" /><div>Secure<br />checkout</div></div>
              <div className="flex items-start gap-2"><BadgeCheck size={15} className="mt-0.5 flex-shrink-0 text-[#9D5C6C]" /><div>Genuine<br />goods</div></div>
              <div className="flex items-start gap-2"><Headset size={15} className="mt-0.5 flex-shrink-0 text-[#9D5C6C]" /><div>Friendly<br />support</div></div>
            </div>

            {/* Accordion */}
            <div className="mt-12 divide-y divide-[#C7BFAB] border-t border-[#C7BFAB]">
              {[
                ["description", "Description", product.description || product.short_description || "No description available."],
                ["specs", "Specifications"],
                ["delivery", "Delivery & returns", "Ships within 1-2 business days. Free standard delivery on orders over $75. 30-day returns on unused items."],
              ].map(([k, label, body]) => {
                const open = openSection === k;
                // Same comma/slash readability fix as on the H1
                const prettyBody = typeof body === "string"
                  ? body.replace(/,(\S)/g, ", $1").replace(/\/(\S)/g, " / $1")
                  : body;
                return (
                  <div key={k}>
                    <button onClick={() => setOpenSection(open ? "" : k)} className="flex w-full items-center justify-between py-6 text-left" data-testid={`accordion-${k}`}>
                      <span
                        className="text-[12px] uppercase tracking-[0.22em] text-black"
                        style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", fontWeight: 600 }}
                      >{label}</span>
                      <ChevronDown size={20} className={`text-black transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="pb-6 text-[15px] leading-[1.75] text-[#3A2E33]" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                        {k === "specs" ? (
                          <dl className="grid gap-2 md:grid-cols-2">
                            {product.sku && <Row k="SKU" v={product.sku} />}
                            {product.upc && <Row k="UPC" v={product.upc} />}
                            {product.manufacturer && <Row k="Manufacturer" v={product.manufacturer} />}
                            {product.model && <Row k="Model" v={product.model} />}
                            {Object.entries(attributes).slice(0, 14).map(([k2, v]) => <Row key={k2} k={k2} v={v} />)}
                          </dl>
                        ) : prettyBody}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Frequently bought together */}
        {product.frequently_bought_together?.length > 0 && (
          <FrequentlyBoughtTogether
            mainProduct={product}
            items={product.frequently_bought_together}
          />
        )}

        {/* Related */}
        {product.related?.length > 0 && (
          <section className="mt-24">
            <div className="mb-8 flex items-end justify-between">
              <h2 className="font-display text-3xl md:text-4xl">You might also <span className="font-display-soft">like</span></h2>
              <Link to="/catalog" className="hidden items-center gap-1 text-sm text-[#9D5C6C] hover:underline md:inline-flex">See more <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4">
              {product.related.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}

        {/* Recently Viewed */}
        <RecentlyViewed excludeId={product.id} limit={4} />
      </div>

      {/* Sticky mobile Add-to-Bag bar (hidden on desktop) */}
      <div
        className="fixed bottom-16 left-0 right-0 z-20 border-t border-[#C7BFAB] bg-white/95 px-4 py-3 backdrop-blur-md md:hidden"
        data-testid="product-mobile-sticky-cta"
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-[#3A2E33]">{product.title}</div>
            <div className="store-nums text-[17px] font-semibold text-black">{formatPrice(product.price, product.currency)}</div>
          </div>
          <button
            onClick={handleAdd}
            disabled={soldOut}
            className={`flex-shrink-0 rounded-full px-5 py-3 text-[14px] font-semibold text-white transition-colors ${added ? "bg-[#9D5C6C]" : "bg-black hover:bg-[#9D5C6C]"} disabled:bg-[#5D5448]/40`}
            data-testid="mobile-sticky-add-btn"
          >
            {soldOut ? "Sold out" : (added ? "✓ Added" : "Add to bag")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-[#C7BFAB]/70 py-2">
      <dt className="font-medium text-[#5D5448]">{k}</dt>
      <dd className="break-words text-right font-semibold text-black">{String(v)}</dd>
    </div>
  );
}
